import { createSidebar } from '@lib/plugins/sidebar';
import auth from '@lib/plugins/auth';
import { extensionMenu } from '@lib/extensions';

import type { SidebarItemType } from '@lib/stores/app-sidebar-menu';
import type { useAppContextStore } from '@lib/plugins/layouts/context';
import type { AuthUser } from '@lib/types';

/**
 * The menu of the active team and event. Entries guarded by a plain global permission carry
 * `permissions` and are filtered by the lib; seller, event and settings entries depend on the
 * context or mix a context permission with a global one and are decided here. Dividers and
 * headers are placed unconditionally, the lib drops the ones whose section ends up empty.
 * See lib/docs/sidebar.md.
 */
function buildSidebarMenu(user: AuthUser, context: ReturnType<typeof useAppContextStore>): Array<SidebarItemType> {
	const [activeTeam, activeEvent] = context.getCurrentContext;
	const isSeller = !!activeTeam && !!activeEvent && user.active_context?.[1]?.data?.pivot?.is_seller == 1;
	const mapModule = (globalThis.config?.['app.enable_map_module'] || '0') == '1';

	const menu: Array<SidebarItemType> = [];

	if (activeTeam && activeEvent) {
		menu.push(
			{ to: '/dashboard', icon: 'fa-home', label: 'dashboard' },
			{ to: '/shop', icon: 'fa-shop', label: 'shop' },
			{ to: '/cart', icon: 'fa-shopping-cart', label: 'cart' },
			{ to: '/orders', icon: 'fa-tasks', label: 'orders' },
			{ to: '/delivery', icon: 'fa-truck', label: 'delivery' },
			// The extensions' entries of the main section, none yet
			...extensionMenu('main', user, context),
		);
	} else {
		if (!activeTeam) menu.push({ label: 'no_team_selected' });
		if (!activeEvent) menu.push({ label: 'no_event_selected' });
	}

	if (isSeller) {
		menu.push(
			{ divider: true },
			{ label: 'seller_header', values: { name: activeTeam.name } },
			{ to: '/seller/articles', icon: ['fa-clipboard-list', 'fa-right'], label: 'seller_articles' },
			{ to: '/seller/orders', icon: ['fa-tasks', 'fa-right'], label: 'seller_orders' },
			{ to: '/seller/order_items', icon: ['fa-cubes', 'fa-right'], label: 'seller_order_items' },
			{ to: '/seller/delivery', icon: ['fa-truck', 'fa-right'], label: 'seller_delivery' },
		);
		if (mapModule) menu.push({ to: '/seller/map', icon: 'fa fa-map', label: 'seller_map' });
	}

	if (activeEvent && (auth.checkContext('orders.read', 'Event') || auth.check('events.read'))) {
		menu.push(
			{ divider: true },
			{ label: 'event_header', values: { name: activeEvent.name } },
			{ to: '/event/articles', icon: ['fa-clipboard-list', 'fa-campground'], label: 'event_articles' },
			{ to: '/event/orders', icon: ['fa-tasks', 'fa-campground'], label: 'event_orders' },
			{ to: '/event/order_items', icon: ['fa-cubes', 'fa-campground'], label: 'event_order_items' },
			{ to: '/event/delivery', icon: ['fa-truck', 'fa-campground'], label: 'event_delivery' },
		);
		if (mapModule) menu.push({ to: '/event/map', icon: 'fa fa-map', label: 'event_map' });
	}

	menu.push({ divider: true }, { label: 'settings' });
	if (activeEvent && (auth.checkContext('event.write', 'Event') || auth.check('events.write'))) {
		menu.push({
			to: '/event/settings',
			icon: ['fa fa-cog', 'fa-campground'],
			label: 'event_settings',
			values: { event_name: activeEvent.name || '' },
		});
	}
	if (activeTeam && (auth.checkContext('team.write', 'Team') || auth.check('teams.write'))) {
		menu.push({
			to: '/team/settings',
			icon: ['fa fa-cog', 'fa-building'],
			label: 'team_settings',
			values: { team_name: activeTeam.name || '' },
		});
	}

	menu.push(
		{ divider: true },
		{ label: 'admin_header' },
		{ to: '/admin/users', icon: 'fa fa-users', label: 'users', permissions: ['users.read'] },
		{ to: '/admin/teams', icon: 'fa fa-building', label: 'teams', permissions: ['teams.read'] },
		{ to: '/admin/events', icon: 'fa fa-campground', label: 'events', permissions: ['events.read'] },
		{ to: '/admin/settings', icon: 'fa fa-cogs', label: 'settings', permissions: ['app.settings'] },
		{
			to: '/admin/maintenance',
			icon: 'fa fa-screwdriver-wrench',
			label: 'maintenance',
			permissions: ['app.developer'],
		},
		{ href: '/docs/swagger', icon: 'fa-book', label: 'swagger', opener: true, permissions: ['app.developer'] },
		// The extensions' admin pages, e.g. the MCP server's info page
		...extensionMenu('admin', user, context),
	);

	return menu;
}

export default createSidebar({ menu: buildSidebarMenu, badges: 'me/sidebar_badges' });
